import React, { Component, Fragment } from 'react';
import { matchPath } from 'react-router';
import { withRouter } from './util/withRouter';
import { createTheme } from '@mui/material/styles';
import Header from './component/header/Header';
import AppNavDrawer from './component/drawer/AppNavDrawer';
import Footer from './component/footer/Footer';
import Main from './page/Main';
import { isSignedIn } from './util/sessions';
import { getOfficeFromKey, getOfficeFromURLSegment } from './util/brands';
import { SnackbarMessageConsumer, withSnackbarMessage } from './context/SnackbarMessage';
import { compose } from './reactApolloCompat';
import { getCurrentUser, getOffices, loadSiteConfig } from './environment';
import { WhiteLoader } from './component/WhiteLoader';
import cloneDeep from 'lodash.clonedeep';
import { recolour, theme } from './Theme';
import { AppThemeProvider, MainThemeProvider } from './Providers';
import { Box } from '@mui/material';

class App extends Component {
    state = {
        signedIn: false,
        drawerOpen: false,
        drawerMinimized: false,
        funeralHome: undefined,
        loadedConfig: false
    };

    static getDerivedStateFromProps(newProps, oldState) {
        const { location } = newProps;
        const newState = {};
        const signedIn = isSignedIn();
        if (signedIn !== oldState.signedIn) {
            newState.signedIn = signedIn;
        }

        let funeralHome;

        let searchmatch = false;
        if ((searchmatch = matchPath({ path: '/search/:brand', end: false }, location.pathname))) {
            const urlSegment = searchmatch.params.brand;
            funeralHome = urlSegment && getOfficeFromURLSegment(urlSegment);
        } else if ((searchmatch = matchPath({ path: '/funeral/:key/:id', end: false }, location.pathname))) {
            const key = searchmatch && searchmatch.params && searchmatch.params.key;
            funeralHome = key && getOfficeFromKey(key);
        } else if ((searchmatch = matchPath({ path: '/documentation/:key/:id', end: false }, location.pathname))) {
            const key = searchmatch && searchmatch.params && searchmatch.params.key;
            funeralHome = key && getOfficeFromKey(key);
        } else if ((searchmatch = matchPath({ path: '/prearrangement/:key/:id', end: false }, location.pathname))) {
            const key = searchmatch && searchmatch.params && searchmatch.params.key;
            funeralHome = key && getOfficeFromKey(key);
        } else if ((searchmatch = matchPath({ path: '/mortuary/:junk/:key/:id', end: false }, location.pathname))) {
            const key = searchmatch && searchmatch.params && searchmatch.params.key;
            funeralHome = key && getOfficeFromKey(key);
        } else if ((searchmatch = matchPath({ path: '/transfers/:key/:id', end: false }, location.pathname))) {
            const key = searchmatch && searchmatch.params && searchmatch.params.key;
            funeralHome = key && getOfficeFromKey(key);
        } else if ((searchmatch = matchPath({ path: '/quotes/enquiry/:key/:id', end: false }, location.pathname))) {
            const key = searchmatch && searchmatch.params && searchmatch.params.key;
            funeralHome = key && getOfficeFromKey(key);
        } else if ((searchmatch = matchPath({ path: '/quotes', end: false }, location.pathname))) {
            const urlParams = new URLSearchParams(window.location.search);
            const key = urlParams.get('office');
            funeralHome = key && getOfficeFromKey(key);
        } else if (['/', '/account', '/work-queue-cards'].includes(location.pathname)) {
            const { MyLastOfficeID } = getCurrentUser();
            const isFound = MyLastOfficeID && getOffices().find(obj => obj.ID === MyLastOfficeID);
            funeralHome = isFound && {
                ID: isFound.ID,
                letterCode: isFound.RegistrantSuffix,
                label: isFound.Title,
                logo: isFound.Logo,
                color: isFound.OfficeColour || isFound.ForegroundColour,
                style: 'tablestyle' + isFound.ID
            };
        }

        if (funeralHome !== oldState.funeralHome) {
            newState.funeralHome = funeralHome;
        }

        if (Object.keys(newState).length) {
            return newState;
        }
        return null;
    }

    getBaseTheme(baseTheme) {
        const allOffices = getOffices().map(({ ID, RegistrantSuffix, OfficeColour, ForegroundColour }) => ({
            ID,
            letterCode: RegistrantSuffix,
            color: OfficeColour || ForegroundColour
        }));

        const themed = createTheme(baseTheme, {
            palette: {
                contentBackground: Object.fromEntries([
                    ...allOffices.map(({ letterCode, color }) => [letterCode, recolour(color, '#FFFFFF', 0.9)]),
                    ['none', '#EBF6FD']
                ]),
                contentForeground: Object.fromEntries([
                    ...allOffices.map(({ letterCode, color }) => [letterCode, color]),
                    ['none', '#35327c']
                ]),
                basePrimary: Object.fromEntries([
                    ...allOffices.map(({ letterCode, color }) => [letterCode, color]),
                    ['none', '#35327c']
                ]),
                baseSecondary: Object.fromEntries([
                    ...allOffices.map(({ letterCode, color }) => [letterCode, recolour(color, '#000000', 0.3)]),
                    ['none', '#413BBE']
                ]),
                baseTertiary: Object.fromEntries([
                    ...allOffices.map(({ letterCode, color }) => [letterCode, recolour(color, '#FFFFFF', 0.4)]),
                    ['none', '#92A5E8']
                ]),
                primary: { main: '#35327c' },
                secondary: { main: '#e64040' },
                action: { active: '#413BBE' }
            },
            funeralHome: 'none'
        });

        const head = document.head;
        if (!head.querySelector('#theme-css') && allOffices.length) {
            const styleSheet = document.createElement('style');
            styleSheet.rel = 'stylesheet';
            styleSheet.id = 'theme-css';
            head.appendChild(styleSheet);
            [...allOffices, { ID: 0, letterCode: 'none' }].forEach(obj => {
                const tableStyle = `tablestyle${obj.ID}`;
                const cDark = themed.palette.contentForeground[obj.letterCode];
                const cLight = themed.palette.contentBackground[obj.letterCode];
                [
                    `table.table tr.row--${tableStyle} { outline-color: ${cDark}; }`,
                    `table.table tr.row--header.row--${tableStyle} > th { background-color: ${cDark}; border-color: ${cDark}; }`,
                    `table.table tr.row--${tableStyle} > .td { background-color: ${cLight}; border-color: ${cDark}66; border-left-color: ${cDark}33; background-image: radial-gradient(#FFFFFF99, transparent) }`,
                    `table.table:not(.no-responsive) tr.row.row--${tableStyle} > .td[data-label]::before { background-color: ${cDark}; }`,
                    `table.table tr.row--${tableStyle}:hover > .td { background-color: ${cLight}44; }`,
                    `table.table tr.row--${tableStyle} > .td svg, .table .row--${tableStyle} > .td a, .table .row--${tableStyle} > .td a > span { color: ${cDark}; }`,
                    `@media only screen and (max-width: 760px), (min-device-width: 768px) and (max-device-width: 1024px) { table.table tr.row--${tableStyle} > .td:after { border-color: ${cDark}; } }`,
                    `.table-key--${tableStyle} { background-color: ${cLight}; color: ${cDark}; border-color: ${cDark}; }`
                ].forEach(rule => styleSheet.sheet.insertRule(rule, styleSheet.sheet.cssRules.length));
            });
        }

        return themed;
    }

    getBrandTheme(fmTheme) {
        const { funeralHome } = this.state;
        return createTheme(fmTheme, {
            funeralHome: (funeralHome && funeralHome.letterCode) || 'none',
            palette: funeralHome
                ? {
                      primary: { main: funeralHome.color },
                      secondary: { main: fmTheme.palette.baseSecondary[funeralHome.letterCode] },
                      action: { active: funeralHome.color }
                  }
                : {}
        });
    }

    onToggleDrawerOpen = () => {
        this.setState({ drawerOpen: !this.state.drawerOpen });
    };

    onToggleDrawerMinimized = () => {
        this.setState({ drawerMinimized: !this.state.drawerMinimized });
    };

    render() {
        const { signedIn, loadedConfig } = this.state;
        if (signedIn && !loadedConfig) {
            loadSiteConfig().then(() => this.setState({ loadedConfig: true })); // load siteConfig into cache
        }
        const fmTheme = this.getBaseTheme(theme);
        const brandTheme = this.getBrandTheme(cloneDeep(fmTheme));
        return (
            <Box
                component="div"
                sx={{
                    paddingBottom: `${signedIn ? theme.sizes.footerHeight : 0}px`,
                    display: 'flex',
                    width: '100%',
                    height: '100dvh' // account for browser ui
                }}
            >
                <AppThemeProvider theme={fmTheme}>
                    {signedIn && (
                        <Fragment>
                            <Header
                                onToggleDrawerOpen={this.onToggleDrawerOpen}
                                onToggleDrawerMinimized={this.onToggleDrawerMinimized}
                                drawerMinimized={this.state.drawerMinimized}
                            />
                            <AppNavDrawer
                                onToggleOpen={this.onToggleDrawerOpen}
                                onToggleMinimized={this.onToggleDrawerMinimized}
                                open={this.state.drawerOpen}
                                minimized={this.state.drawerMinimized}
                            />
                        </Fragment>
                    )}
                    {!signedIn || loadedConfig ? (
                        <MainThemeProvider theme={brandTheme}>
                            <Main signedIn={signedIn} />
                        </MainThemeProvider>
                    ) : (
                        <div style={{ margin: 'auto' }}>
                            <WhiteLoader actionWord="Loading" />
                        </div>
                    )}
                    <SnackbarMessageConsumer />
                    {signedIn && <Footer />}
                </AppThemeProvider>
            </Box>
        );
    }
}

export default compose(withSnackbarMessage, withRouter)(App);
